import { marketLabel } from '../lib/protocol';
import { leagueTitle } from '../lib/sports';

/**
 * Название матча для строки и витрины.
 *
 * Зеркало Марка часто кладёт в `event` свой id (`d51955…-7`). Лента 5 метит
 * вилку `v5:f:…`. Раньше заголовок падал на `row.id` — человек видел хеш
 * вместо команд. Сюда не берём ни код, ни прочерк: нет имени — слово «матч».
 */

const CODE_NAME =
  /^(?:[a-f0-9]{16,}(?:-\d+)?|(?:v\d+:)?f:[a-f0-9]+|[0-9a-f]{8}-[0-9a-f-]{27})$/i;

export function isCodeName(raw: string | null | undefined): boolean {
  const text = (raw ?? '').trim();
  return text === '' || text === '—' || text === '-' || text === '?' || CODE_NAME.test(text);
}

/** Адаптер иногда кладёт в участников ссылку на матч — это не имя команды. */
export function isUrlName(raw: string | null | undefined): boolean {
  const text = (raw ?? '').trim();
  return /^https?:\/\//i.test(text) || /^www\./i.test(text);
}

const NAMED: Record<string, string> = {
  amp: '&',
  quot: '"',
  apos: "'",
  lt: '<',
  gt: '>',
  nbsp: ' ',
};

function fromCode(n: number): string {
  if (!Number.isInteger(n) || n < 1 || n > 0x10ffff) return '';
  if (n >= 0xd800 && n <= 0xdfff) return '';
  return String.fromCodePoint(n);
}

function decodeOnce(raw: string): string {
  return raw
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => fromCode(Number.parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec: string) => fromCode(Number(dec)))
    .replace(/&(amp|quot|apos|lt|gt|nbsp);/gi, (all, name: string) => NAMED[name.toLowerCase()] ?? all);
}

/** Контора шлёт Honv&#233;d — в ленте должно быть Honvéd, не код. */
export function decodeName(raw: string): string {
  const first = decodeOnce(raw);
  return first.includes('&') ? decodeOnce(first) : first;
}

function clean(list: readonly string[] | null | undefined): string[] {
  return (list ?? [])
    .map((one) => decodeName(one).replace(/\s+/g, ' ').trim())
    .filter((one) => !isCodeName(one) && !isUrlName(one));
}

type Titled = {
  id?: string;
  row?: {
    fork?: {
      participants?: readonly string[] | null;
      legs?: readonly { participants?: readonly string[] | null }[];
    };
  };
};

function namesOf(row: Titled): string[] {
  const fork = row.row?.fork;
  for (const list of [
    fork?.participants,
    fork?.legs?.[0]?.participants,
    fork?.legs?.[1]?.participants,
  ]) {
    const parts = clean(list);
    if (parts.length > 0) return parts;
  }
  return [];
}

export function matchTitle(row: Titled): string {
  const parts = namesOf(row);
  if (parts.length >= 2) return parts.slice(0, 2).join(' — ');
  if (parts.length === 1) return parts[0] ?? 'матч';
  return 'матч';
}

/** В поиск и в буфер — без тире, как копирует карточка. */
export function matchWords(row: Titled): string {
  const parts = namesOf(row);
  return parts.length > 0 ? parts.join(' ') : 'матч';
}

/**
 * Подпись шапки карточки: лига и рынок, та же фраза, что в строке ленты.
 *
 * Без рынка карточка слепа: в ленте исход стоит в подзаголовке строки,
 * а взгляд уже на плитках. Семейство «другой» человеку не помогает.
 */
export function cardKind(fork: {
  sport?: string;
  sportName?: string;
  league?: string;
  market?: string;
}): string {
  const league = leagueTitle(fork);
  const market = marketLabel(fork.market, fork.sport);
  return [league, market].filter((one) => one !== '' && one !== '—').join(' · ');
}
